// The shared login/sign-up form body — the reusable heart of "login via email". Modes: signin, create
// (→ logIn / signUp), verify (the "check your email" state). The password never leaves the device (see
// account.ts). This renders just the form content; the modal / panel wrappers add the surrounding chrome.
//
// On a successful sign-in it runs the entitlement check FIRST (unentitled → redirect to onboarding/plan
// selection), then hands the UnlockedAccount to `onAuthenticated` — where the host trades the wiser-accounts
// session for its own native session (e.g. POST /auth/wiser). The host decides whether to persist a session
// (session.ts / cache.ts) — the form deliberately does not, so auth-only apps stay stateless.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  signUp, logIn, EmailNotVerifiedError, DeviceChallengeRequiredError, completeDeviceChallenge,
  revokeRefresh, type UnlockedAccount,
} from './account';
import { getAccountsApi } from './accountsApi';
import { loadLastUsername, saveLastUsername, clearLastUsername } from './cache';
import { keychainAvailable, saveRefreshToken, clearRefreshToken, loadRefreshToken } from './secretStore';
import { alwaysEntitled, type EntitlementCheck } from './entitlement';
import { RecoveryCodeCard } from './RecoveryCodeCard';
import { ResetPasswordForm } from './ResetPasswordForm';
import type { AccountsApi } from '@wiser/accounts';
import * as s from './styles';

export type FormMode = 'signin' | 'create';

/** True when the error is a connection-level fetch failure (offline / server unreachable), as opposed to a
 *  response the service actually sent. Browsers throw a TypeError ("Failed to fetch" / "Load failed" / …). */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError
    || (err instanceof Error && /failed to fetch|load failed|network/i.test(err.message));
}

export interface AccountFormProps {
  /** Injectable for tests / a local service; defaults to the app-configured client (configureAccountsApi). */
  api?: AccountsApi;
  /** Called after a successful sign-in + entitlement check. Trade `account.token` for your native session
   *  here (and persist the session/keys if you're a crypto app). May be async; errors surface inline. */
  onAuthenticated: (account: UnlockedAccount) => void | Promise<void>;
  /** Gate access by plan/entitlement. Default: everyone in. Unentitled → redirect to `onboardUrl`. */
  entitlement?: EntitlementCheck;
  /** Show the "create an account" toggle + signup flow. Default true. */
  allowSignup?: boolean;
  initialMode?: FormMode;
  /** Passed to signup (org/workspace scope). */
  tenantId?: string;
  /** Invite code sent with signup (the wiser-accounts service is invite-gated). Preset by the host, or
   *  collect it from the user in your own chrome and pass it here. */
  inviteCode?: string;
  /** Sub-heading copy override. */
  subtitle?: string;
  /** For modal usage — called after the flow finishes so the modal can close. */
  onDone?: () => void;
  /** Remember the last-used email and pre-fill it on the next visit, so a returning user only types their
   *  password. Non-secret; persisted via the `cache.ts` BlobStore seam. Default true. */
  rememberUsername?: boolean;
  /** Offer a "keep me signed in on this device" checkbox — but ONLY where an OS keychain exists (Tauri
   *  desktop with a `SecretStore` wired; never on the web). When checked, sign-in stores a revocable refresh
   *  token in the keychain (never the password) so the session is silently restored next launch. Default
   *  true (i.e. offered wherever a keychain is available). */
  allowKeepSignedIn?: boolean;
  /** Audit provenance (0022): the dashboard/app this login screen belongs to (e.g. "selfserve", "console").
   *  Recorded in the sign-in audit trail on the server; it is NOT a service-usage signal and does not affect
   *  behavior. Optional — omit and the login is audited as "unknown". */
  client?: string;
}

export function AccountForm({
  api = getAccountsApi(), onAuthenticated, entitlement = alwaysEntitled,
  allowSignup = true, initialMode = 'signin', tenantId, inviteCode, subtitle, onDone,
  rememberUsername = true, allowKeepSignedIn = true, client,
}: AccountFormProps) {
  const [mode, setMode] = useState<'signin' | 'create' | 'verify' | 'recovery' | 'reset' | 'device_challenge'>(initialMode);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The "check your email" verify state: the email a link was sent to + a short cool-down after Resend.
  const [verifyEmail, setVerifyEmail] = useState('');
  const [resent, setResent] = useState(false);
  // The one-time recovery code generated at signup — shown (and acknowledged) before the verify step.
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  // New-device challenge: the emailed PIN the user types to approve this device.
  const [challengePin, setChallengePin] = useState('');
  // Whether the email field was pre-filled from the remembered last-used username (drives the "Not you?"
  // affordance). Tracks user typing so the async pre-fill never clobbers input.
  const [prefilled, setPrefilled] = useState(false);
  const typedRef = useRef(false);
  // "Keep me signed in" — only shown once we confirm an OS keychain exists (async). Off by default.
  const [canKeepSignedIn, setCanKeepSignedIn] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);

  // Detect a keychain (Tauri desktop with a wired SecretStore). No-ops to false on the web.
  useEffect(() => {
    if (!allowKeepSignedIn) return;
    let cancelled = false;
    void keychainAvailable().then((ok) => { if (!cancelled) setCanKeepSignedIn(ok); });
    return () => { cancelled = true; };
  }, [allowKeepSignedIn]);

  // Pre-fill the last-used email (returning user → just enter a password). Effect-only, so it no-ops during
  // SSR and never overwrites what the user has already typed.
  useEffect(() => {
    if (!rememberUsername) return;
    let cancelled = false;
    void loadLastUsername().then((saved) => {
      if (cancelled || !saved || typedRef.current) return;
      setEmail((cur) => (cur === '' ? saved : cur));
      if (!typedRef.current) setPrefilled(true);
    });
    return () => { cancelled = true; };
  }, [rememberUsername]);

  const onEmailChange = (v: string) => { typedRef.current = true; setPrefilled(false); setEmail(v); };
  // "Not you?" — forget the remembered username AND drop any stored keychain credential (revoke server-side,
  // best-effort) so switching accounts doesn't leave a live refresh token behind.
  const forgetUsername = () => {
    setEmail(''); setPrefilled(false); typedRef.current = true;
    if (rememberUsername) void clearLastUsername();
    void (async () => {
      const rt = await loadRefreshToken();
      if (rt) await revokeRefresh(api, rt);
      await clearRefreshToken();
    })();
  };

  // Shared post-login success path (entitlement gate → host token exchange → close).
  const finishAuth = async (acct: UnlockedAccount) => {
    const ent = await entitlement(acct);
    if (!ent.entitled) { window.location.assign(ent.onboardUrl); return; }
    if (rememberUsername) void saveLastUsername(acct.account.email);
    // Persist the refresh token iff the user opted in AND the service minted one (0017). Otherwise make sure
    // no stale credential lingers.
    if (keepSignedIn && acct.refreshToken) await saveRefreshToken(acct.refreshToken);
    else if (keepSignedIn) await clearRefreshToken();
    await onAuthenticated(acct);
    onDone?.();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === 'create') {
        // Signup does NOT sign the user in: the account is created UNVERIFIED and the service emails a link.
        const res = await signUp(api, email.trim(), password, { displayName: displayName.trim() || undefined, tenantId, inviteCode });
        setVerifyEmail(res.email); setResent(false);
        // Show the recovery code first (save-once) if one was minted; otherwise straight to "check your email".
        if (res.recoveryCode) { setRecoveryCode(res.recoveryCode); setMode('recovery'); }
        else setMode('verify');
      } else {
        await finishAuth(await logIn(api, email.trim(), password, keepSignedIn, client));
      }
    } catch (err) {
      console.error('wiser-auth', err);
      if (err instanceof EmailNotVerifiedError) {
        // Login refused because the email isn't verified — show the "check your email" state so they can resend.
        setVerifyEmail(err.email); setResent(false); setMode('verify');
      } else if (err instanceof DeviceChallengeRequiredError) {
        // Correct password from an unrecognised device — a PIN was emailed; collect it.
        setChallengePin(''); setError(null); setMode('device_challenge');
      } else if (isNetworkError(err)) {
        setError('Could not connect to the account server — check that you are online.');
      } else {
        setError(mode === 'create'
          ? 'Could not create the account — it may already exist, or the service is unreachable.'
          : 'Sign-in failed — check your email and password.');
      }
    } finally {
      setBusy(false);
    }
  };

  // Complete the new-device challenge: confirm the emailed PIN (password is still in state) → session.
  const finishChallenge = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await finishAuth(await completeDeviceChallenge(api, email.trim(), password, challengePin, keepSignedIn));
    } catch (err) {
      console.error('wiser-auth', err);
      setError(isNetworkError(err)
        ? 'Could not connect to the account server — check that you are online.'
        : 'That code was incorrect or expired. Check the email, or sign in again to get a new code.');
    } finally {
      setBusy(false);
    }
  };

  // Resend the verification link (best-effort); briefly disable the button so it can't be hammered.
  const resend = async () => {
    setBusy(true); setError(null);
    try {
      await api.resendVerification(verifyEmail);
      setResent(true);
      setTimeout(() => setResent(false), 8000);
    } catch (err) {
      console.error('wiser-auth resend', err);
      setError('Could not resend the verification email — try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const backToSignIn = () => { setMode('signin'); setError(null); setPassword(''); setResent(false); setRecoveryCode(null); };

  const titleText = mode === 'create' ? 'Create your WiserWeb account'
    : mode === 'verify' ? 'Check your email' : 'Sign in';
  const subText = subtitle ?? (mode === 'verify'
    ? 'Confirm your email address to finish setting up your account.'
    : 'One WiserWeb account for every app.');

  // Forgot-password reset flow (request PIN → reset with PIN + recovery code).
  if (mode === 'reset') {
    return (
      <ResetPasswordForm
        api={api}
        initialEmail={email}
        onBack={backToSignIn}
        onReset={(em) => { setEmail(em); backToSignIn(); }}
      />
    );
  }

  // One-time recovery-code display right after signup — must be acknowledged before the verify step.
  if (mode === 'recovery' && recoveryCode) {
    return (
      <RecoveryCodeCard
        code={recoveryCode}
        email={verifyEmail}
        onConfirm={() => { setRecoveryCode(null); setMode('verify'); }}
      />
    );
  }

  if (mode === 'device_challenge') {
    return (
      <>
        <h2 style={s.title}>Verify this device</h2>
        <p style={s.sub}>A one-time code was emailed to <strong>{email}</strong>.</p>
        <form onSubmit={finishChallenge} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
            You're signing in from a device we don't recognise. Enter the code to approve it — this device is
            then remembered for next time.
          </p>
          <input
            style={s.input}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Verification code"
            value={challengePin}
            onChange={(e) => setChallengePin(e.target.value)}
            autoFocus
          />
          {error && <p role="alert" style={s.err}>{error}</p>}
          <button type="submit" disabled={busy || !challengePin.trim()} style={s.primaryBtn}>
            {busy ? '…' : 'Verify & sign in'}
          </button>
          <button type="button" style={s.switchBtn} onClick={backToSignIn}>Back to sign in</button>
        </form>
      </>
    );
  }

  if (mode === 'verify') {
    return (
      <>
        <h2 style={s.title}>{titleText}</h2>
        <p style={s.sub}>{subText}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.45 }}>
            We sent a verification link to <strong>{verifyEmail}</strong>. Click it to activate your account,
            then come back and sign in.
          </p>
          {resent && <p role="status" style={{ margin: 0, fontSize: 13, color: 'var(--wui-text-muted, #6b7280)' }}>Sent again — check your inbox (and spam).</p>}
          {error && <p role="alert" style={s.err}>{error}</p>}
          <button type="button" disabled={busy || resent} style={s.primaryBtn} onClick={() => void resend()}>
            {busy ? '…' : resent ? 'Email sent' : 'Resend email'}
          </button>
          <button type="button" style={s.switchBtn} onClick={backToSignIn}>Back to sign in</button>
        </div>
      </>
    );
  }

  return (
    <>
      <h2 style={s.title}>{titleText}</h2>
      <p style={s.sub}>{subText}</p>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label style={s.lbl}>Email
          <input type="email" autoComplete="username" required value={email}
            onChange={(e) => onEmailChange(e.target.value)} style={s.input}
            autoFocus={mode === 'signin' && !prefilled} />
        </label>
        {mode === 'signin' && prefilled && email && (
          <button type="button" style={s.linkBtn} onClick={forgetUsername}>
            Not {email}? Use a different account
          </button>
        )}
        {mode === 'create' && (
          <label style={s.lbl}>Name <span style={{ opacity: 0.5 }}>(optional)</span>
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={s.input} />
          </label>
        )}
        <label style={s.lbl}>Password
          <input type="password" required minLength={mode === 'create' ? 8 : undefined}
            autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
            value={password} onChange={(e) => setPassword(e.target.value)} style={s.input} />
        </label>
        {mode === 'create' && (
          <p style={s.warn}>🔑 We’ll give you a one-time <strong>recovery code</strong> right after this — save it. It’s how you reset a forgotten password without losing your encrypted data.</p>
        )}
        {mode === 'signin' && (
          <button type="button" style={s.linkBtn} onClick={() => { setMode('reset'); setError(null); }}>
            Forgot your password?
          </button>
        )}
        {mode === 'signin' && canKeepSignedIn && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={keepSignedIn} onChange={(e) => setKeepSignedIn(e.target.checked)} />
            Keep me signed in on this device
          </label>
        )}
        {error && <p role="alert" style={s.err}>{error}</p>}
        <button type="submit" disabled={busy} style={s.primaryBtn}>
          {busy ? '…' : mode === 'create' ? 'Create account' : 'Sign in'}
        </button>
      </form>
      {allowSignup && (
        <button type="button" style={s.switchBtn}
          onClick={() => { setMode(mode === 'create' ? 'signin' : 'create'); setError(null); }}>
          {mode === 'create' ? 'Already have an account? Sign in' : 'New here? Create an account'}
        </button>
      )}
    </>
  );
}
